import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import SophisticatedTooltipLink from '@wix/da-ds/pkg/tooltips/SophisticatedTooltipLink';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useSubmitField } from '../../hooks/useSubmitField';

import s from './ScrapsToggleField.scss';

interface Props {
  className?: string;
}

const ScrapsToggleField: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const {
    id,
    field: { onChange, value },
  } = useSubmitField('isScrap', {
    initAdapter: ({ deviation }) => deviation?.extended?.isScrap ?? false,
    submitAdapter: ({ isScrap }) => ({ is_scrap: isScrap }),
  });

  const scrapsTooltip = isMobile ? (
    <PlainButtonOrLink
      className={s['label-scraps-link']}
      rel="noopener noreferrer"
      target="_blank"
      href={t('deviationSubmit.form.scrapsToggle.link.url')}
    >
      {t('deviationSubmit.form.scrapsToggle.tooltip.opener')}
    </PlainButtonOrLink>
  ) : (
    <SophisticatedTooltip
      size="medium"
      aria-label={t('deviationSubmit.form.scrapsToggle.tooltip.opener')}
      description={t('deviationSubmit.form.scrapsToggle.tooltip.text')}
      hasCloseButton={false}
      postfix={
        <SophisticatedTooltipLink
          rel="noopener noreferrer"
          target="_blank"
          href={t('deviationSubmit.form.scrapsToggle.link.url')}
        >
          {t('common.readMore')}
        </SophisticatedTooltipLink>
      }
    >
      <span className={s['label-scraps-link']}>
        {t('deviationSubmit.form.scrapsToggle.tooltip.opener')}
      </span>
    </SophisticatedTooltip>
  );

  return (
    <ButtonFieldLayout
      label={
        <>
          {t('deviationSubmit.form.scrapsToggle.label.start')}
          {scrapsTooltip}
        </>
      }
      labelProps={{ id: `${id}-label`, htmlFor: id }}
    >
      <MainToggle
        variant="main"
        id={id}
        size={isMobile ? 'large' : 'small'}
        value={value}
        onChange={onChange}
        aria-labelledby={`${id}-label`}
      />
    </ButtonFieldLayout>
  );
};
ScrapsToggleField.displayName = 'ScrapsToggleField';

export default ScrapsToggleField;
